import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import TerminalService from "app/data/terminal-details/terminalService";
import TerminalSummaryService from "app/data/terminal-summary/terminalsummaryService";
import type { TerminalSummaryModel } from "app/data/terminal-summary/TerminalListModel";
import { userSelector } from "slices/user/userSlice";

const terminalService = TerminalService.getInstance();
const terminalSummaryService = TerminalSummaryService.getInstance();

/** Stable map marker id for the active terminal (service center). */
export const PICKUP_ASSIGNMENT_TERMINAL_MARKER_ID = "pa-map-current-terminal";

export type ActiveTerminalMapLocation = {
  lat: number;
  lng: number;
  name: string;
  airportCode?: string;
  addressLine?: string;
};

function coordsFromSummaryRow(t: TerminalSummaryModel): ActiveTerminalMapLocation | null {
  const lat = Number(t.address?.latitude);
  const lng = Number(t.address?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const line = [t.address.physicalAddress, t.address.city, t.address.stateProvince].filter(Boolean).join(", ");
  return {
    lat,
    lng,
    name: t.terminalName,
    airportCode: t.airportCode || undefined,
    addressLine: line || undefined,
  };
}

function coordsFromTerminalDetailPayload(data: Record<string, unknown>): ActiveTerminalMapLocation | null {
  const addr = data.address as Record<string, unknown> | undefined;
  if (!addr || typeof addr !== "object") return null;
  const lat = Number(addr.latitude);
  const lng = Number(addr.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const name = typeof data.terminalName === "string" ? data.terminalName : "Terminal";
  const airportCode = typeof data.airportCode === "string" ? data.airportCode : undefined;
  const line = [addr.physicalAddress, addr.city, addr.stateProvince]
    .map((x) => (typeof x === "string" ? x : ""))
    .filter(Boolean)
    .join(", ");
  return {
    lat,
    lng,
    name,
    airportCode,
    addressLine: line || undefined,
  };
}

/**
 * Loads coordinates for the user's active terminal from terminal master data:
 * tries GET `/terminals/:number` first, then paginated `/terminals` list (search by terminal number).
 */
async function fetchActiveTerminalLocation(terminalId: number): Promise<ActiveTerminalMapLocation | null> {
  const detail = await terminalService.getTerminalSummaryByTerminalNumber(String(terminalId));
  if (detail.ok() && detail.data && typeof detail.data === "object") {
    const fromDetail = coordsFromTerminalDetailPayload(detail.data as Record<string, unknown>);
    if (fromDetail) return fromDetail;
  }

  const listRes = await terminalSummaryService.getTerminalsList(0, String(terminalId), null, "ACTIVE", null, null);
  if (listRes.isCancel?.()) return null;
  if (!listRes.ok() || !listRes.data?.content?.length) return null;
  const rows = listRes.data.content as TerminalSummaryModel[];
  const row = rows.find((r) => r.terminalNumber === terminalId) ?? rows[0];
  return coordsFromSummaryRow(row);
}

export function usePickupAssignmentActiveTerminalLocation() {
  const terminalId = useSelector(userSelector).activeTerminal?.id;

  const q = useQuery({
    queryKey: ["pickupAssignmentV2", "activeTerminalLocation", terminalId],
    queryFn: () => fetchActiveTerminalLocation(terminalId!),
    enabled: terminalId != null && Number.isFinite(Number(terminalId)),
    staleTime: 10 * 60 * 1000,
  });

  return {
    location: q.data ?? null,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}
